import { useEffect, useRef, useState } from "react";
import { TvShareDialog } from "@/components/TvShareDialog";
import { VideoExportDialog } from "@/components/VideoExportDialog";
import { useParams, useNavigate } from "react-router-dom";
import { api, TRANSITIONS, ASPECT_RATIOS, indexById, refreshPosterSlides } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SlideShow } from "@/components/SlideShow";
import { Poster } from "@/components/PosterCanvas";
import { BannerImageRow } from "@/components/BannerImageRow";
import { CategoryFilter, UncategorizedAlert, useCategories, groupImages, posterCategoryId, categoryDuration } from "@/components/categories";
import { CycleDuration } from "@/components/CycleDuration";
import { UploadCategoryDialog } from "@/components/UploadCategoryDialog";
import { BulkCategoryBar } from "@/components/BulkCategoryBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Play, Save, Loader2, Wand2, ImagePlus, Megaphone, ChevronDown,
} from "lucide-react";

const padFor = (ratio) => ASPECT_RATIOS.find((r) => r.value === ratio)?.pad || "56.25%";

const TRANSITION_SPEEDS = [0.3, 0.5, 0.8, 1.2, 2, 3];

export default function BannerEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrincipal = user?.role === "principal";
  const fileRef = useRef(null);
  const [banner, setBanner] = useState(null);
  const [images, setImages] = useState([]);
  const { categories } = useCategories();
  const [catFilter, setCatFilter] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [pendingFiles, setPendingFiles] = useState(null);
  const setGrouped = (fn) => setImages((prev) => groupImages(fn(prev), categories));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posterDialog, setPosterDialog] = useState(false);
  const [posters, setPosters] = useState(null);
  const [defaultTransition, setDefaultTransition] = useState("fade");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setDefaultTransition(data.default_transition || "fade")).catch(() => {});
  }, []);

  const openPosterPicker = async () => {
    setPosterDialog(true);
    if (posters === null) {
      try {
        const { data } = await api.get("/posters");
        setPosters(data);
      } catch {
        setPosters([]);
      }
    }
  };

  const addPoster = (poster) => {
    setGrouped((prev) => [...prev, {
      id: crypto.randomUUID(),
      type: "poster",
      poster_data: poster,
      url: "",
      duration: categoryDuration(categories, posterCategoryId(categories)) || 10,
      transition: defaultTransition,
      category_id: posterCategoryId(categories),
      order: 0,
    }]);
    setPosterDialog(false);
    toast.success("Affiche ajoutée");
  };

  useEffect(() => {
    Promise.all([api.get(`/banners/${id}`), api.get("/posters").catch(() => ({ data: [] }))])
      .then(([b, pr]) => {
        const map = indexById(pr.data);
        const imgs = refreshPosterSlides(
          [...(b.data.images || [])].sort((a, z) => (a.order ?? 0) - (z.order ?? 0)),
          map
        );
        setBanner(b.data);
        setImages(imgs);
      })
      .catch(() => {
        toast.error("Bandeau introuvable");
        navigate("/");
      });
  }, [id, navigate]);

  const applyTransitionDurationToAll = (value) => {
    setImages((prev) => prev.map((img) => ({ ...img, transition_duration: value })));
    toast.success(`Durée de transition ${value}s appliquée à toutes les diapositives — pensez à enregistrer`);
  };

  const applyTransitionToAll = (value) => {
    setImages((prev) => prev.map((img) => ({ ...img, transition: value })));
    const label = TRANSITIONS.find((t) => t.value === value)?.label || value;
    toast.success(`Transition « ${label} » appliquée à toutes les diapositives — pensez à enregistrer`);
  };

  const pickFiles = (files) => {
    if (files?.length) setPendingFiles(Array.from(files));
    if (fileRef.current) fileRef.current.value = "";
  };

  const autoSave = useRef(false);
  useEffect(() => {
    if (autoSave.current) save();
  }, [images]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = async (files, categoryId) => {
    setPendingFiles(null);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({
          id: crypto.randomUUID(),
          url: data.url,
          storage_path: data.storage_path,
          original_filename: data.original_filename,
          duration: categoryDuration(categories, categoryId) || 5,
          transition: defaultTransition,
          category_id: categoryId,
          order: 0,
        });
      }
      autoSave.current = true;
      setGrouped((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} image(s) ajoutée(s) — enregistrement…`);
    } catch (e) {
      toast.error("Échec de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const withCategoryDuration = (patch) => {
    const d = "category_id" in patch ? categoryDuration(categories, patch.category_id) : null;
    return d ? { ...patch, duration: d } : patch;
  };

  const updateImage = (imgId, patch) =>
    ("category_id" in patch ? setGrouped : setImages)((prev) => prev.map((img) => (img.id === imgId ? { ...img, ...withCategoryDuration(patch) } : img)));

  const toggleSelected = (imgId) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(imgId)) next.delete(imgId); else next.add(imgId);
    return next;
  });

  const applyCategory = (categoryId) => {
    setGrouped((prev) => prev.map((img) => (selected.has(img.id) ? { ...img, ...withCategoryDuration({ category_id: categoryId }) } : img)));
    toast.success(`Catégorie appliquée à ${selected.size} image${selected.size > 1 ? "s" : ""} — pensez à enregistrer`);
  };

  const applyDuration = (duration) => {
    setImages((prev) => prev.map((img) => (selected.has(img.id) ? { ...img, duration } : img)));
    toast.success(`Durée de ${duration}s appliquée à ${selected.size} image${selected.size > 1 ? "s" : ""} — pensez à enregistrer`);
  };

  const visibleImages = images.filter((img) => catFilter === null || (img.category_id || "") === catFilter);

  const removeImage = (imgId) =>
    setImages((prev) => prev.filter((img) => img.id !== imgId).map((img, i) => ({ ...img, order: i })));

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const reorder = (from, to) => {
    setImages((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr.map((img, i) => ({ ...img, order: i }));
    });
  };

  const onDrop = (to) => {
    const sameCategory = dragIndex !== null && (images[dragIndex]?.category_id || "") === (images[to]?.category_id || "");
    if (dragIndex !== null && dragIndex !== to && !sameCategory) toast.warning("Déplacement possible uniquement dans la même catégorie");
    else if (dragIndex !== null && dragIndex !== to) reorder(dragIndex, to);
    setDragIndex(null);
    setOverIndex(null);
  };

  const save = async () => {
    autoSave.current = false;
    setSaving(true);
    try {
      const payload = {
        name: banner.name,
        aspect_ratio: banner.aspect_ratio,
        loop: banner.loop,
        images: images.map((img, i) => ({ ...img, order: i })),
      };
      const { data } = await api.put(`/banners/${id}`, payload);
      setBanner(data);
      toast.success("Enregistré");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!banner) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay pointer-events-none fixed inset-0 opacity-60" />

      <header className="sticky top-11 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Input
              value={banner.name}
              onChange={(e) => setBanner({ ...banner, name: e.target.value })}
              disabled={!isPrincipal}
              className="max-w-xs border-transparent bg-transparent font-display text-lg font-bold focus-visible:border-input disabled:opacity-100"
              data-testid="editor-name-input"
            />
            <CycleDuration images={images} categories={categories} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <TvShareDialog bannerId={id} />
            {images.length > 0 && <VideoExportDialog bannerId={id} beforeStart={save} />}
            <Button
              variant="secondary"
              disabled={!images.length}
              onClick={() => navigate(`/play/${id}`)}
              data-testid="editor-play-button"
            >
              <Play className="mr-1 h-4 w-4" /> Lire
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-banner-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-5 lg:px-8">
        {/* left: preview + settings */}
        <div className="space-y-5 lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-3 font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
              Aperçu en direct
            </p>
            <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: padFor(banner.aspect_ratio) }}>
              <SlideShow images={images} loop={banner.loop} />
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
            <div className="space-y-2">
              <Label>Format d'affichage</Label>
              <Select
                value={banner.aspect_ratio}
                onValueChange={(v) => setBanner({ ...banner, aspect_ratio: v })}
                disabled={!isPrincipal}
              >
                <SelectTrigger data-testid="editor-ratio-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIOS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Lecture en boucle</p>
                <p className="text-xs text-muted-foreground">Recommencer après la dernière image</p>
              </div>
              <Switch
                checked={banner.loop}
                onCheckedChange={(v) => setBanner({ ...banner, loop: v })}
                disabled={!isPrincipal}
                data-testid="editor-loop-switch"
              />
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => pickFiles(e.target.files)}
            data-testid="file-input"
          />
          <UploadCategoryDialog files={pendingFiles} categories={categories} onCancel={() => setPendingFiles(null)} onConfirm={handleFiles}
            onReplace={(i, f) => setPendingFiles((l) => l.map((x, j) => (j === i ? f : x)))} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border py-10 text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
            data-testid="upload-media-dropzone"
          >
            {uploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <Upload className="h-8 w-8" />}
            <span className="font-display font-semibold">{uploading ? "Envoi en cours…" : "Importer des photos"}</span>
            <span className="text-xs">Depuis votre appareil · JPG, PNG, WEBP</span>
          </button>

          <Button variant="secondary" className="w-full" onClick={openPosterPicker} data-testid="add-poster-slide-button">
            <Megaphone className="mr-2 h-4 w-4" /> Ajouter une affiche
          </Button>

          <Dialog open={posterDialog} onOpenChange={setPosterDialog}>
            <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="poster-picker-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Choisir une affiche</DialogTitle>
              </DialogHeader>
              {posters === null ? (
                <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : posters.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Aucune affiche. Créez-en une depuis l'onglet « Affiches ».
                </div>
              ) : (
                <div className="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2">
                  {posters.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addPoster(p)}
                      className="overflow-hidden rounded-lg border border-border text-left transition-colors hover:border-primary"
                      data-testid={`pick-poster-${p.id}`}
                    >
                      <div className="aspect-video w-full bg-black">
                        <Poster data={p} className="h-full w-full" />
                      </div>
                      <p className="truncate px-3 py-2 text-sm font-medium">{p.name}</p>
                    </button>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* right: image sequence */}
        <div className="lg:col-span-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold">Séquence d'images</h2>
            <div className="flex items-center gap-3">
              {images.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" data-testid="apply-transition-menu-button" title="Appliquer une transition à toutes les diapositives">
                      <Wand2 className="mr-1 h-3.5 w-3.5" /> Transition à tout
                      <ChevronDown className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Appliquer à toutes les diapositives</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {TRANSITIONS.map((t) => (
                      <DropdownMenuItem key={t.value} onClick={() => applyTransitionToAll(t.value)} data-testid={`apply-transition-${t.value}`}>
                        {t.label}{t.value === defaultTransition ? " · défaut" : ""}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Durée de transition (toutes)</DropdownMenuLabel>
                    {TRANSITION_SPEEDS.map((d) => (
                      <DropdownMenuItem key={d} onClick={() => applyTransitionDurationToAll(d)} data-testid={`apply-transition-duration-${d}`}>
                        {d}s{d === 0.8 ? " · défaut" : ""}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <span className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                {images.length} élément{images.length > 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {images.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-20 text-center text-muted-foreground">
              <ImagePlus className="mb-3 h-10 w-10" />
              <p className="font-display font-semibold">Aucune image</p>
              <p className="text-sm">Importez des photos pour construire votre bandeau.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <UncategorizedAlert count={images.filter((i) => !i.category_id).length} onShow={() => {
                setCatFilter("");
                setSelected(new Set(images.filter((i) => !i.category_id && (isPrincipal || !i.locked)).map((i) => i.id)));
              }} />
              <CategoryFilter categories={categories} images={images} value={catFilter} onChange={(v) => { setCatFilter(v); setSelected(new Set()); }} />
              <BulkCategoryBar categories={categories} selectable={visibleImages.filter((i) => isPrincipal || !i.locked)}
                selected={selected} setSelected={setSelected} onApply={applyCategory} onApplyDuration={applyDuration} />
              {images.map((img, idx) => (catFilter !== null && (img.category_id || "") !== catFilter) ? null : (
                <BannerImageRow key={img.id} img={img} idx={idx} categories={categories} isPrincipal={isPrincipal}
                  selected={selected.has(img.id)} onToggle={toggleSelected}
                  dragIndex={dragIndex} overIndex={overIndex} setDragIndex={setDragIndex} setOverIndex={setOverIndex}
                  onDrop={onDrop} updateImage={updateImage} removeImage={removeImage} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
